import React, { useState, lazy, Suspense } from 'react';
import { FolderOpen, Upload, File, Download, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import DragDropZone from '../DragDropZone';

// Lazy-load the preview modal — pulls in pdf.js/mammoth/markdown only when needed
const DocumentPreview = lazy(() => import('./DocumentPreview'));

export default function BandFilesSection({ bandFiles, onUploadFile, onDeleteFile, isMobile }) {
  const [expanded, setExpanded] = useState(false);
  const [fileTitle, setFileTitle] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const handleFileSelect = (file) => {
    setPendingFile(file);
    setFileTitle(file.name.replace(/\.[^/.]+$/, ''));
  };

  const handleUpload = async () => {
    if (!pendingFile || !fileTitle.trim()) return;
    try {
      setUploading(true);
      await onUploadFile(pendingFile, fileTitle);
      setPendingFile(null); setFileTitle('');
    } catch (err) {
      // Error handled by parent
    } finally { setUploading(false); }
  };

  return (
    <div className="card-3d rounded-card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left"
      >
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-white" />
          Band Files
          {bandFiles.length > 0 && (
            <span className="bg-white/10 text-white text-xs px-2 py-0.5 rounded-full">{bandFiles.length}</span>
          )}
        </h2>
        {expanded ? <ChevronUp className="w-5 h-5 text-text-subtle" /> : <ChevronDown className="w-5 h-5 text-text-subtle" />}
      </button>

      {expanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6">
          <p className="text-sm text-text-subtle mb-4">
            Upload any files your band needs — contracts, logos, reference tracks, whatever.
          </p>

          {!pendingFile ? (
            <DragDropZone
              accept="*"
              onFileSelect={handleFileSelect}
              disabled={uploading}
              icon={Upload}
              message="Drag and drop any file here, or click to browse"
              isMobile={isMobile}
              className="mb-4"
            />
          ) : (
            <div className="border border-surface-border-light rounded-card-btn p-4 mb-4">
              <p className="text-sm text-text-muted mb-3">
                Selected: <span className="font-semibold">{pendingFile.name}</span>
              </p>
              <input
                type="text" value={fileTitle} onChange={(e) => setFileTitle(e.target.value)}
                placeholder="Enter a title for this file..."
                className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand text-sm mb-3 rounded-pill"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleUpload}
                  disabled={uploading || !fileTitle.trim()}
                  className="bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition px-4 py-2 font-semibold text-sm flex items-center gap-2 rounded-pill"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
                <button
                  onClick={() => { setPendingFile(null); setFileTitle(''); }}
                  disabled={uploading}
                  className="bg-white/10 hover:bg-white/20 transition px-4 py-2 font-semibold text-sm rounded-pill"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {bandFiles.length === 0 ? (
            <p className="text-text-subtle text-sm">No files yet.</p>
          ) : (
            <div className="space-y-2">
              {bandFiles.map(file => (
                <div key={file.id} className="border border-surface-border-light rounded-card-btn p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-white truncate">{file.title}</p>
                    <p className="text-xs text-text-subtle truncate">{file.fileName} • {file.fileSize} KB</p>
                    <p className="text-xs text-text-subtle">
                      By {file.uploadedByName} • {new Date(file.uploadDate).toLocaleDateString('nl-NL')}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-3">
                    <button
                      onClick={() => setPreviewFile(file)}
                      className="bg-transparent hover:bg-brand transition p-2 rounded-full"
                      title="Preview"
                    >
                      <File className="w-4 h-4 text-white" />
                    </button>
                    <a href={file.fileURL} download={file.fileName} className="bg-transparent hover:bg-brand transition p-2 rounded-full" title="Download">
                      <Download className="w-4 h-4 text-white" />
                    </a>
                    <button onClick={() => onDeleteFile(file.id, file.storagePath)} className="bg-transparent hover:bg-red-600 transition p-2 rounded-full" title="Delete">
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview modal */}
      {previewFile && (
        <Suspense fallback={null}>
          <DocumentPreview file={previewFile} onClose={() => setPreviewFile(null)} />
        </Suspense>
      )}
    </div>
  );
}
